import React, { ReactNode } from "react"

import { MetricHelpIconTooltip } from "@/components/ui/metric-tooltip"
import { cn } from "@/lib/utils"
import { dashboardMutedTextClassName, dashboardSidebarSurfaceCardClassName } from "./dashboardChartCardShell"
import {
  liftedTitleCardHeightClass,
  liftedTitleCardPaddingClass,
  liftedTitleCardUniformHeight,
} from "./liftedTitleCardUtils"

interface LiftedTitleCardProps {
  title: string | ReactNode
  children: ReactNode
  height?: { base?: string; sm?: string; md?: string; lg?: string; xl?: string }
  tooltip?: string
  timeframe?: string
  onTimeframeChange?: (timeframe: string) => void
  backgroundGradient?: string
  outerCardGradient?: string
  titlePadding?: {
    pl?: { base?: string; sm?: string; md?: string; lg?: string; xl?: string }
    pr?: { base?: string; sm?: string; md?: string; lg?: string; xl?: string }
    py?: { base?: string; sm?: string; md?: string; lg?: string; xl?: string }
  }
  rightElement?: ReactNode
  bottomMessage?: string
  hideBottomSection?: boolean
  /** Hide the mobile title-bar footer strip without compact title styling. */
  suppressMobileTitleBarFooter?: boolean
  useNormalStyling?: boolean
}

const timeframeSelectClass =
  "w-auto min-w-0 cursor-pointer appearance-none border-none bg-transparent pr-4 font-[family-name:'IBM_Plex_Sans',sans-serif] text-[10px] font-medium uppercase focus:outline-none focus:ring-0 sm:text-xs md:text-sm " +
  dashboardMutedTextClassName

function TimeframeSelect({
  value,
  onChange,
  className,
}: {
  value?: string
  onChange?: (value: string) => void
  className?: string
}) {
  if (!onChange) return null
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(timeframeSelectClass, className)}
      aria-label="Timeframe"
    >
      <option value="today">Today</option>
      <option value="7-days">7 days</option>
      <option value="14-days">14 days</option>
      <option value="30-days">30 days</option>
      <option value="all-time">All time</option>
    </select>
  )
}

const LiftedTitleCard: React.FC<LiftedTitleCardProps> = ({
  title,
  children,
  height = { base: "140px", sm: "160px", md: "185px", lg: "185px", xl: "185px" },
  tooltip,
  timeframe,
  onTimeframeChange,
  outerCardGradient,
  titlePadding,
  rightElement,
  bottomMessage,
  hideBottomSection = false,
  suppressMobileTitleBarFooter = false,
  useNormalStyling = false,
}) => {
  const effectiveStyling = useNormalStyling ? false : hideBottomSection
  const titleOnGradient = Boolean(outerCardGradient)
  const titleClassName = cn(
    "m-0 min-w-0 flex-1 text-left text-xs font-medium uppercase leading-tight sm:text-xs md:text-sm",
    titleOnGradient
      ? "font-[family-name:'IBM_Plex_Sans',sans-serif] text-white"
      : cn("font-[family-name:var(--font-primary)]", dashboardMutedTextClassName)
  )

  const titleBarPadding = cn(
    liftedTitleCardPaddingClass("pl", titlePadding?.pl, "pl-1 sm:pl-1.5 md:pl-2"),
    liftedTitleCardPaddingClass("pr", titlePadding?.pr, "pr-1 sm:pr-1.5 md:pr-2"),
    liftedTitleCardPaddingClass(
      "py",
      titlePadding?.py,
      effectiveStyling ? "py-2" : "py-1 sm:py-1.5 md:py-2 lg:py-1"
    )
  )

  const uniformHeight = liftedTitleCardUniformHeight(height)

  return (
    <div
      data-dashboard-lifted-card=""
      data-dashboard-sidebar-surface-card=""
      className={cn(
        "flex w-full flex-col overflow-hidden rounded-[6px] border border-border bg-card shadow-none",
        dashboardSidebarSurfaceCardClassName,
        liftedTitleCardHeightClass(height),
        effectiveStyling ? "gap-0 pt-2 pb-0" : "gap-0 pt-1 pb-0 lg:gap-1"
      )}
      style={{
        ...(outerCardGradient ? { background: outerCardGradient } : undefined),
        ...(uniformHeight ? { height: uniformHeight } : undefined),
      }}
    >
      <div className={cn("flex min-w-0 items-center justify-between gap-2", titleBarPadding)}>
        {typeof title === "string" ? (
          <span
            {...(!outerCardGradient ? { "data-dashboard-mini-stat-title": "" } : {})}
            className={titleClassName}
          >
            {title}
          </span>
        ) : (
          <div className="flex min-h-0 min-w-0 flex-1 items-center">{title}</div>
        )}

        {rightElement ? (
          <div className="flex h-full min-h-8 shrink-0 items-center justify-center self-center">
            {rightElement}
          </div>
        ) : (
          <div className="ml-2 hidden min-w-fit shrink-0 items-center gap-2 lg:flex">
            {onTimeframeChange ? (
              <TimeframeSelect value={timeframe} onChange={onTimeframeChange} />
            ) : bottomMessage ? (
              <span className={cn("font-[family-name:'IBM_Plex_Sans',sans-serif] text-xs font-medium uppercase sm:text-xs md:text-sm", dashboardMutedTextClassName)}>
                {bottomMessage}
              </span>
            ) : (
              <span className="invisible min-w-[90px]" aria-hidden />
            )}
          </div>
        )}

        {tooltip ? (
          <MetricHelpIconTooltip
            description={tooltip}
            label={typeof title === "string" ? title : undefined}
            iconClassName="size-3.5"
          />
        ) : null}
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col overflow-visible">
        {children}

        {!hideBottomSection && !suppressMobileTitleBarFooter ? (
          <div className="absolute right-0 bottom-0 left-0 z-2 flex h-6 min-h-6 items-center justify-start gap-2 px-1 sm:h-7 sm:min-h-7 sm:px-1.5 md:h-8 md:min-h-8 md:px-2 lg:hidden">
            {onTimeframeChange ? (
              <TimeframeSelect value={timeframe} onChange={onTimeframeChange} />
            ) : bottomMessage ? (
              <span className={cn("font-[family-name:'IBM_Plex_Sans',sans-serif] text-xs font-medium uppercase", dashboardMutedTextClassName)}>
                {bottomMessage}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default LiftedTitleCard
